/**
 * TPG Player shell (React) — formerly referred to as NextGenPlayer.
 *
 * Official product name: TPG Player (The Poletti Group Player).
 * Class / file name retained in Sprint 8 Phase A to avoid risky production renames.
 * Prefer documenting and configuring this surface as TPG Player.
 */
import React, { Component } from "react";
import { createPlaybackEngine } from "../Core/PlaybackEngine";
import { createPlaybackFacade } from "../Core/PlaybackFacade";
import { PlayerEvent } from "../Core/PlayerEvents";
import { PlaybackPlatformEvent } from "../Core/PlaybackPlatformEvents";
import { createPluginManager } from "../Plugins/PluginManager";
import { resolvePlayerConfig } from "../Services/SourceResolver";
import { bindKeyboardShortcuts } from "../Services/KeyboardShortcutService";
import { createTrackController } from "../Services/TrackController";
import { createActivityMonitor } from "../Services/ActivityMonitor";
import { createSpatialFocusManager } from "../Services/SpatialFocusManager";
import {
  resolveTheaterMode,
  persistTheaterMode,
  resolveFullscreenOnFirstPlay,
  persistFullscreenOnFirstPlay,
  watermarkPositionClass,
} from "../Services/playerPreferences";
import { createTheme } from "../Themes/createTheme";
import {
  ensureAppCastingSession,
  CastingEvent,
  CastingTarget,
} from "../Providers/CastingProvider";
import MediaSurface from "./MediaSurface";
import ControlBar from "../Controls/ControlBar";
import { PlaybackState } from "../Core/PlaybackState";
import {
  attemptRestoreAudio,
  armUnmuteOnNextUserGesture,
  applyUnmuteToEngine,
} from "../production/autoplayPolicy";
import { tpgTrace } from "../production/tpgPipelineTrace";
import "../Themes/player.css";

/**
 * Stable identity for the playable media. Host config object identity often
 * changes every render; reloading on reference churn resets ABR and seek.
 */
function sourceIdentityKey(resolved) {
  if (!resolved || !resolved.source || !resolved.source.src) {
    return "";
  }
  return (
    String(resolved.source.src) +
    "|" +
    String(resolved.source.type || "") +
    "|" +
    String(resolved.contentId || "")
  );
}

/**
 * Next-generation player shell — isolated from production JW player.
 *
 * Phase 4: CastingManager (Chromecast / AirPlay / simulated), remote state
 * sync into UI + plugins, TV spatial focus foundation.
 */
class NextGenPlayer extends Component {
  constructor(props) {
    super(props);
    this.shellRef = React.createRef();
    this.engine = null;
    this.pluginHost = null;
    this.tracks = null;
    this.activity = null;
    this.casting = null;
    this.spatial = null;
    this.unsubKeyboard = null;
    this.unsubActivity = null;
    this._unsubs = [];
    this._busUnsubs = [];
    this._castUnsubs = [];
    this._lastRemotePlaying = null;

    this._disposeUnmuteArm = null;
    this._loadTimeoutId = null;
    this._facade = null;
    this._firstPlayFullscreenDone = false;
    this._watermarkRevealTimer = null;
    /** Surface may attach via refs before componentDidMount creates the engine. */
    this._pendingSurface = null;
    this._surfaceAttached = false;

    var initialResolved = resolvePlayerConfig(props.config || {});
    this.state = {
      snapshot: {
        playbackState: PlaybackState.IDLE,
        currentTime: 0,
        duration: 0,
        bufferedEnd: 0,
        volume: 1,
        muted: false,
        playbackRate: 1,
        error: null,
        quality: { levels: [], mode: "auto", currentLevel: -1 },
        providerName: null,
        castTarget: CastingTarget.LOCAL,
      },
      resolved: initialResolved,
      textTracks: [],
      activeTextId: null,
      audioTracks: [],
      activeAudioId: null,
      chapters: [],
      controlsVisible: true,
      announcement: "",
      castActive: false,
      castProviderName: null,
      // Host may own theater (post-handoff); fall back to persisted preference
      theaterMode:
        props.theaterMode != null
          ? !!props.theaterMode
          : resolveTheaterMode(
              initialResolved.features && initialResolved.features.theaterDefault
            ),
      watermarkRevealed: false,
      loadTimedOut: false,
      surfaceReady: false,
    };

    this.handleAttach = this.handleAttach.bind(this);
    this.handlePlayPause = this.handlePlayPause.bind(this);
    this.handleSeek = this.handleSeek.bind(this);
    this.handleSkip = this.handleSkip.bind(this);
    this.handleVolume = this.handleVolume.bind(this);
    this.handleToggleMute = this.handleToggleMute.bind(this);
    this.handleRate = this.handleRate.bind(this);
    this.handleFullscreen = this.handleFullscreen.bind(this);
    this.handleTheater = this.handleTheater.bind(this);
    this.handleRetry = this.handleRetry.bind(this);
    this.handlePiP = this.handlePiP.bind(this);
    this.handleQualityChange = this.handleQualityChange.bind(this);
    this.handleTextTrackChange = this.handleTextTrackChange.bind(this);
    this.handleAudioTrackChange = this.handleAudioTrackChange.bind(this);
    this.handleChapterSelect = this.handleChapterSelect.bind(this);
    this.handleSeekZone = this.handleSeekZone.bind(this);
    this.handleCast = this.handleCast.bind(this);
    this._getMergedState = this._getMergedState.bind(this);
    this._transport = {
      play: function () {
        return this.casting ? this.casting.play() : this.engine.play();
      }.bind(this),
      pause: function () {
        return this.casting ? this.casting.pause() : this.engine.pause();
      }.bind(this),
      seek: function (t) {
        return this.casting ? this.casting.seek(t) : this.engine.seek(t);
      }.bind(this),
      setVolume: function (v) {
        return this.casting
          ? this.casting.setVolume(v)
          : this.engine.setVolume(v);
      }.bind(this),
      setMuted: function (m) {
        return this.casting
          ? this.casting.setMuted(m)
          : this.engine.setMuted(m);
      }.bind(this),
    };
  }

  _clearLoadTimeout() {
    if (this._loadTimeoutId) {
      clearTimeout(this._loadTimeoutId);
      this._loadTimeoutId = null;
    }
  }

  _armLoadTimeout(resolved) {
    var self = this;
    this._clearLoadTimeout();
    var ms =
      (resolved.features && resolved.features.loadTimeoutMs) || 45000;
    if (!ms || ms < 1000) return;
    this._loadTimeoutId = setTimeout(function () {
      var st = self._getMergedState();
      if (
        st.playbackState === PlaybackState.PLAYING ||
        st.playbackState === PlaybackState.PAUSED ||
        st.playbackState === PlaybackState.ENDED
      ) {
        return;
      }
      self.setState({ loadTimedOut: true });
      if (self.pluginHost) {
        self.pluginHost.bus.emit(PlayerEvent.ANNOUNCEMENT, {
          message: "Playback is taking longer than expected",
        });
      }
    }, ms);
  }

  _onPlaybackStartedUx() {
    var self = this;
    this._clearLoadTimeout();
    if (this.state.loadTimedOut) {
      this.setState({ loadTimedOut: false });
    }
    if (this._watermarkRevealTimer) {
      clearTimeout(this._watermarkRevealTimer);
    }
    this._watermarkRevealTimer = setTimeout(function () {
      self.setState({ watermarkRevealed: true });
    }, 600);
  }

  _emitHost(name, payload) {
    if (typeof this.props.onPlaybackEvent === "function") {
      this.props.onPlaybackEvent(name, payload);
    }
  }

  _getMergedState() {
    var local = this.engine ? this.engine.getState() : {};
    if (!this.casting || !this.casting.isRemote()) {
      return Object.assign({}, local, { castTarget: CastingTarget.LOCAL });
    }
    var remote = this.casting.getRemoteState() || {};
    return Object.assign({}, local, {
      castTarget: CastingTarget.REMOTE,
      currentTime:
        remote.currentTime != null ? remote.currentTime : local.currentTime,
      duration: remote.duration || local.duration,
      playbackState: remote.playing
        ? PlaybackState.PLAYING
        : PlaybackState.PAUSED,
      volume: remote.volume != null ? remote.volume : local.volume,
      muted: remote.muted != null ? remote.muted : local.muted,
      playbackRate:
        remote.playbackRate != null ? remote.playbackRate : local.playbackRate,
    });
  }

  _refreshSnapshot() {
    this.setState({ snapshot: this._getMergedState() });
  }

  _bindKeyboard() {
    var resolved = this.state.resolved;
    if (
      !this.shellRef.current ||
      !resolved.features.keyboardShortcuts ||
      this.unsubKeyboard
    ) {
      return;
    }
    this.unsubKeyboard = bindKeyboardShortcuts(
      this.engine,
      this.shellRef.current,
      {
        enabled: true,
        seekStep: resolved.features.skipInterval || 10,
        bus: this.pluginHost.bus,
        transport: this._transport,
        getState: this._getMergedState,
        skipSpatialArrows: !!resolved.features.tvNavigation,
      }
    );
  }

  _setupSpatialFocus() {
    var resolved = this.state.resolved;
    if (this.spatial) {
      this.spatial.destroy();
      this.spatial = null;
    }
    if (
      !resolved.features.tvNavigation ||
      !this.shellRef.current
    ) {
      return;
    }
    var keyMap =
      (resolved.casting && resolved.casting.remoteKeyMap) ||
      resolved.features.remoteKeyMap ||
      null;
    this.spatial = createSpatialFocusManager(this.shellRef.current, {
      enabled: true,
      initialFocus: "play",
      keyMap: keyMap,
      onAction: function (action) {
        if (action === "playpause") {
          this.handlePlayPause();
          return true;
        }
        if (action === "play") {
          this._transport.play();
          return true;
        }
        if (action === "pause") {
          this._transport.pause();
          return true;
        }
        if (action === "stop") {
          this._transport.pause();
          this._transport.seek(0);
          return true;
        }
        return false;
      }.bind(this),
    });
  }

  _mediaDescriptorFromResolved(resolved) {
    var cfg = this.state.resolved || resolved || {};
    var src = cfg.source || {};
    return {
      src: src.src,
      type: src.type,
      title: cfg.title,
      poster: cfg.poster,
      contentId: cfg.contentId,
    };
  }

  _initCasting(resolved) {
    var self = this;
    if (this.props.castingProvider) {
      // Legacy single-provider inject — wrap lightly via manager-like facade
      this.casting = this.props.castingProvider;
      this._ownsCastingManager = true;
      if (this.casting.initialize) {
        this.casting.initialize().catch(function () {});
      }
      return;
    }

    // App-level session: survives VideoPlayer unmount. Player only attaches.
    var features = resolved.features || {};
    var appSession = ensureAppCastingSession({
      features: {
        castingSimulate: !!features.castingSimulate,
      },
      casting: resolved.casting || {},
    });
    this._appCastingSession = appSession;
    this._ownsCastingManager = false;
    this.casting = appSession.getManager();

    appSession.attachPlayer({
      engine: this.engine,
      tracks: this.tracks,
      getMediaDescriptor: function () {
        return self._mediaDescriptorFromResolved(resolved);
      },
    });

    this._castUnsubs = [
      this.casting.on(CastingEvent.TARGET_CHANGED, function (p) {
        var remote = p.target === CastingTarget.REMOTE;
        self.setState({
          castActive: remote,
          castProviderName: remote
            ? self.casting.getActiveProvider() &&
              self.casting.getActiveProvider().name
            : null,
        });
        self._refreshSnapshot();
        self._emitHost(CastingEvent.TARGET_CHANGED, p);
        if (self.pluginHost) {
          self.pluginHost.bus.emit(CastingEvent.TARGET_CHANGED, p);
          self.pluginHost.bus.emit(PlayerEvent.ANNOUNCEMENT, {
            message: remote
              ? "Casting started"
              : "Playback returned to this device",
          });
        }
      }),
      this.casting.on(CastingEvent.STATE_SYNC, function (payload) {
        if (payload && payload.logOnly) return;
        self._refreshSnapshot();
        var remote = payload && payload.remote;
        if (remote && self.pluginHost) {
          var playing = !!remote.playing;
          if (self._lastRemotePlaying !== playing) {
            self._lastRemotePlaying = playing;
            self.pluginHost.bus.emit(
              playing
                ? PlayerEvent.PLAYBACK_STARTED
                : PlayerEvent.PLAYBACK_PAUSED,
              { remote: true }
            );
          }
          self.pluginHost.bus.emit(PlayerEvent.TIME_UPDATE, {
            currentTime: remote.currentTime,
            duration: remote.duration,
            remote: true,
          });
        }
        self._emitHost(CastingEvent.STATE_SYNC, payload);
      }),
      this.casting.on(CastingEvent.FAILOVER_LOCAL, function (p) {
        self._emitHost(CastingEvent.FAILOVER_LOCAL, p);
        if (self.pluginHost) {
          self.pluginHost.bus.emit(PlayerEvent.ANNOUNCEMENT, {
            message: "Casting ended — resumed locally",
          });
        }
      }),
      this.casting.on(CastingEvent.ERROR, function (p) {
        self._emitHost(CastingEvent.ERROR, p);
      }),
      this.casting.on(CastingEvent.SESSION_STARTED, function (p) {
        self._emitHost(CastingEvent.SESSION_STARTED, p);
      }),
      this.casting.on(CastingEvent.SESSION_ENDED, function (p) {
        self._emitHost(CastingEvent.SESSION_ENDED, p);
      }),
    ];
  }

  componentDidMount() {
    var resolved = this.state.resolved;
    var self = this;
    tpgTrace("PLAYER_MOUNTED", {
      source: resolved.source && resolved.source.src,
      type: resolved.source && resolved.source.type,
      autoplay: resolved.autoplay,
      muted: resolved.muted,
      contentId: resolved.contentId,
    });

    this.tracks = createTrackController();
    this.engine = createPlaybackEngine({
      volume: resolved.volume,
      muted: resolved.muted,
      playbackRate: resolved.playbackRate,
    });
    var facadeOpts = {
      mediaAsset: resolved.mediaAsset || null,
      strategy: resolved.presentationStrategy || null,
      capabilities: resolved.enabledCapabilities || [],
    };
    this._facade = createPlaybackFacade(this.engine, null, facadeOpts);

    this.pluginHost = createPluginManager(this.engine, resolved, {
      tracks: this.tracks,
      getShell: function () {
        return self.shellRef.current;
      },
      getState: this._getMergedState,
    });
    this.pluginHost.registerMany(resolved.plugins);

    this._initCasting(resolved);
    if (this._facade) {
      this._facade = createPlaybackFacade(
        this.engine,
        this.casting,
        facadeOpts
      );
    }

    var refresh = function () {
      self._refreshSnapshot();
    };

    var forward = function (eventName) {
      return self.engine.on(eventName, function (payload) {
        refresh();
        self._emitHost(eventName, payload);
      });
    };

    this._unsubs = [
      this.engine.on("statechange", refresh),
      this.engine.on("timeupdate", refresh),
      this.engine.on("volumechange", function () {
        refresh();
        // Host stage / Skip Intro / mute control unmute via onAudioUnlocked
        // without the shell gesture arm — clear the Branch B tap cue once
        // audio is actually unmuted so the hint does not stick.
        try {
          var vst = self.engine.getState && self.engine.getState();
          if (
            vst &&
            vst.muted === false &&
            self.state.announcement === "Tap anywhere for sound"
          ) {
            self.setState({ announcement: "" });
          }
        } catch (eVol) {
          /* ignore */
        }
      }),
      this.engine.on("ratechange", refresh),
      this.engine.on("buffer", refresh),
      // Keep ControlBar in sync when provider capabilities resolve after handoff
      this.engine.on(PlaybackPlatformEvent.CAPABILITIES_CHANGED, refresh),
      // Host overlays (intro skip, CW) need TimeUpdate — not only local snapshot refresh
      forward(PlayerEvent.TIME_UPDATE),
      this.engine.on(PlayerEvent.PLAYBACK_STARTED, function (payload) {
        refresh();
        self._onPlaybackStartedUx();
        self._emitHost(PlayerEvent.PLAYBACK_STARTED, payload);
      }),
      forward(PlayerEvent.PLAYBACK_PAUSED),
      forward(PlayerEvent.PLAYBACK_ENDED),
      forward(PlayerEvent.BUFFER_STARTED),
      forward(PlayerEvent.BUFFER_ENDED),
      forward(PlayerEvent.SEEK_STARTED),
      forward(PlayerEvent.SEEK_COMPLETED),
      forward(PlayerEvent.SOURCE_CHANGED),
      forward(PlayerEvent.PROVIDER_CHANGED),
      forward(PlayerEvent.RETRY_ATTEMPT),
      forward(PlayerEvent.NETWORK_UPDATE),
      forward(PlayerEvent.FULLSCREEN_CHANGED),
      forward(PlayerEvent.PIP_CHANGED),
      this.engine.on(PlayerEvent.QUALITY_CHANGED, function (q) {
        refresh();
        self._emitHost(PlayerEvent.QUALITY_CHANGED, q);
        if (self.props.onQualityChange) {
          self.props.onQualityChange(q);
        }
      }),
      this.engine.on(PlayerEvent.PROVIDER_SUBTITLES_CHANGED, function (payload) {
        if (
          self.tracks &&
          typeof self.tracks.mergeProviderSubtitleTracks === "function"
        ) {
          self.tracks.mergeProviderSubtitleTracks(
            (payload && payload.tracks) || []
          );
        }
        // Keep captions OFF until the user opts in.
        if (self.engine && typeof self.engine.setProviderSubtitles === "function") {
          var active =
            self.tracks && self.tracks.getActiveTextTrackId
              ? self.tracks.getActiveTextTrackId()
              : null;
          self.engine.setProviderSubtitles(active || "off");
        }
      }),
      this.engine.on("error", function (err) {
        self._clearLoadTimeout();
        refresh();
        self._emitHost(PlayerEvent.PLAYBACK_ERROR, err);
        if (self.props.onError) {
          self.props.onError(err);
        }
      }),
      this.engine.on("ended", function () {
        refresh();
        if (self.props.onEnded) {
          self.props.onEnded();
        }
      }),
      this.engine.on("recovered", function () {
        self._clearLoadTimeout();
        refresh();
      }),
    ];

    var bus = this.pluginHost.bus;
    this._busUnsubs = [
      bus.on(PlayerEvent.TEXT_TRACKS_CHANGED, function (payload) {
        self.setState({
          textTracks: (payload && payload.tracks) || [],
          activeTextId: payload ? payload.activeId : null,
        });
        self._emitHost(PlayerEvent.TEXT_TRACKS_CHANGED, payload);
      }),
      bus.on(PlayerEvent.TEXT_TRACK_SELECTED, function (payload) {
        self.setState({ activeTextId: payload && payload.id });
        if (self.engine && typeof self.engine.setProviderSubtitles === "function") {
          self.engine.setProviderSubtitles((payload && payload.id) || "off");
        }
        self._emitHost(PlayerEvent.TEXT_TRACK_SELECTED, payload);
      }),
      bus.on(PlayerEvent.AUDIO_TRACKS_CHANGED, function (payload) {
        self.setState({
          audioTracks: (payload && payload.tracks) || [],
          activeAudioId: payload ? payload.activeId : null,
        });
        self._emitHost(PlayerEvent.AUDIO_TRACKS_CHANGED, payload);
      }),
      bus.on(PlayerEvent.AUDIO_TRACK_SELECTED, function (payload) {
        self.setState({ activeAudioId: payload && payload.id });
        self._emitHost(PlayerEvent.AUDIO_TRACK_SELECTED, payload);
      }),
      bus.on(PlayerEvent.CHAPTERS_CHANGED, function (payload) {
        self.setState({ chapters: (payload && payload.chapters) || [] });
        self._emitHost(PlayerEvent.CHAPTERS_CHANGED, payload);
      }),
      bus.on(PlayerEvent.ANNOUNCEMENT, function (payload) {
        var msg = (payload && payload.message) || "";
        self.setState({ announcement: msg });
        if (self._announceClearTimer) {
          clearTimeout(self._announceClearTimer);
          self._announceClearTimer = null;
        }
        if (msg) {
          var ttl =
            payload && payload.ttlMs != null ? Number(payload.ttlMs) : 4000;
          if (!(ttl > 0)) {
            ttl = 4000;
          }
          self._announceClearTimer = setTimeout(function () {
            self._announceClearTimer = null;
            self.setState(function (st) {
              if (st.announcement !== msg) {
                return null;
              }
              return { announcement: "" };
            });
          }, ttl);
        }
        self._emitHost(PlayerEvent.ANNOUNCEMENT, payload);
      }),
      bus.on(PlayerEvent.RESUME_OFFERED, function (payload) {
        self._emitHost(PlayerEvent.RESUME_OFFERED, payload);
      }),
      bus.on(PlayerEvent.RESUME_APPLIED, function (payload) {
        self._emitHost(PlayerEvent.RESUME_APPLIED, payload);
      }),
      bus.on(PlayerEvent.PROGRESS_SAVED, function (payload) {
        self._emitHost(PlayerEvent.PROGRESS_SAVED, payload);
      }),
    ];

    if (resolved.features.autoHideControls !== false) {
      this.activity = createActivityMonitor({
        idleMs: resolved.features.controlsIdleMs || 3200,
      });
      this.unsubActivity = this.activity.subscribe(function (active) {
        var playing =
          self._getMergedState().playbackState === PlaybackState.PLAYING;
        self.setState({
          controlsVisible: active || !playing,
        });
      });
      if (this.shellRef.current) {
        this._activityUnbind = this.activity.bind(this.shellRef.current);
      }
    }

    this._bindKeyboard();
    this._setupSpatialFocus();
    if (typeof this.props.onTheaterChange === "function") {
      this.props.onTheaterChange(this.state.theaterMode);
    }

    // Refs often fire before didMount — complete deferred attach + start playback.
    if (this._pendingSurface) {
      this._completeAttach(this._pendingSurface);
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.theaterMode != null &&
      this.props.theaterMode !== prevProps.theaterMode &&
      !!this.props.theaterMode !== !!this.state.theaterMode
    ) {
      this.setState({ theaterMode: !!this.props.theaterMode });
    }
    if (prevProps.config === this.props.config) {
      return;
    }
    var resolved = resolvePlayerConfig(this.props.config || {});
    var prevResolved = resolvePlayerConfig(prevProps.config || {});
    var nextKey = sourceIdentityKey(resolved);
    var prevKey = sourceIdentityKey(prevResolved);
    this.setState({ resolved: resolved });
    if (this.pluginHost && this.pluginHost.updateConfig) {
      this.pluginHost.updateConfig(resolved);
    }
    // Host unlock flips muted true→false without remounting the intro player.
    if (
      this.engine &&
      prevResolved.muted &&
      !resolved.muted
    ) {
      applyUnmuteToEngine(this.engine, resolved.volume);
      tpgTrace("INTRO_PLAYER_SET_MUTED", {
        muted: false,
        via: "componentDidUpdate_config",
        videoMuted: this.engine.getVideoElement
          ? !!(this.engine.getVideoElement() || {}).muted
          : null,
      });
    }
    // Host pages often rebuild config on unrelated setState; only reload media
    // when the playable source identity actually changes.
    if (this.engine && resolved.source && nextKey !== prevKey) {
      this._loadSource(resolved);
      // Content handoff: same Cast session, new media on the receiver.
      if (this._appCastingSession) {
        var self = this;
        this._appCastingSession.attachPlayer({
          engine: this.engine,
          tracks: this.tracks,
          getMediaDescriptor: function () {
            return self._mediaDescriptorFromResolved(resolved);
          },
        });
      }
    }
  }

  componentWillUnmount() {
    tpgTrace("PLAYER_UNMOUNTED", {
      source:
        this.state.resolved &&
        this.state.resolved.source &&
        this.state.resolved.source.src,
      contentId: this.state.resolved && this.state.resolved.contentId,
      playbackState:
        this.state.snapshot && this.state.snapshot.playbackState,
    });
    this._clearLoadTimeout();
    if (this._announceClearTimer) {
      clearTimeout(this._announceClearTimer);
      this._announceClearTimer = null;
    }
    if (this._watermarkRevealTimer) {
      clearTimeout(this._watermarkRevealTimer);
      this._watermarkRevealTimer = null;
    }
    if (this.unsubKeyboard) {
      this.unsubKeyboard();
    }
    if (this._activityUnbind) {
      this._activityUnbind();
    }
    if (this.activity) {
      this.activity.destroy();
    }
    if (this.unsubActivity) {
      this.unsubActivity();
    }
    if (this.spatial) {
      this.spatial.destroy();
    }
    this._unsubs.forEach(function (u) {
      if (typeof u === "function") u();
    });
    this._busUnsubs.forEach(function (u) {
      if (typeof u === "function") u();
    });
    this._castUnsubs.forEach(function (u) {
      if (typeof u === "function") u();
    });
    // App-level Cast: detach local playback only — never end remote session here.
    if (this._appCastingSession) {
      this._appCastingSession.detachPlayer();
      this._appCastingSession = null;
    } else if (
      this._ownsCastingManager &&
      this.casting &&
      this.casting.destroy
    ) {
      this.casting.destroy();
      if (
        typeof window !== "undefined" &&
        window.__TPG_CASTING__ === this.casting
      ) {
        window.__TPG_CASTING__ = null;
        try {
          window.dispatchEvent(new CustomEvent("tpg-casting-changed"));
        } catch (e) {
          /* ignore */
        }
      }
    }
    if (this.pluginHost) {
      this.pluginHost.destroy();
    }
    if (this.tracks) {
      this.tracks.destroy();
    }
    if (this._disposeUnmuteArm) {
      this._disposeUnmuteArm();
      this._disposeUnmuteArm = null;
    }
    if (this.engine) {
      this.engine.destroy();
    }
    this._pendingSurface = null;
    this._surfaceAttached = false;
  }

  handleAttach(surface) {
    this._pendingSurface = surface || null;
    if (!this.engine) {
      // Engine is created in componentDidMount; complete attach there.
      return;
    }
    this._completeAttach(surface);
  }

  /**
   * Attach media surface to the engine and load the current source.
   * Safe to call once engine exists (from didMount or late ref attach).
   */
  _completeAttach(surface) {
    if (!this.engine || !surface) {
      tpgTrace("ERROR", {
        message: "completeAttach_skipped",
        hasEngine: !!this.engine,
        hasSurface: !!surface,
      });
      return;
    }
    var self = this;
    var videoEl = surface.video ? surface.video : surface;
    var embedEl = surface.embed ? surface.embed : null;
    tpgTrace("SURFACE_ATTACHED", {
      hasVideo: !!videoEl,
      hasEmbed: !!embedEl,
      alreadyAttached: this._surfaceAttached,
    });
    if (typeof this.engine.attachMediaSurface === "function") {
      this.engine.attachMediaSurface({ video: videoEl, embed: embedEl });
    } else {
      this.engine.attach(videoEl);
    }
    if (videoEl && this.tracks) {
      this.tracks.attach(videoEl);
    }

    if (this.casting && this.casting.attachMediaElement && videoEl) {
      this.casting.attachMediaElement(videoEl);
    }

    if (this.activity && this.shellRef.current && !this._activityUnbind) {
      this._activityUnbind = this.activity.bind(this.shellRef.current);
    }
    this._bindKeyboard();
    this._setupSpatialFocus();

    var alreadyAttached = this._surfaceAttached;
    this._surfaceAttached = true;
    this.pluginHost.notifyReady();
    this.setState({ surfaceReady: true });
    this._emitHost(PlayerEvent.PLAYER_READY, {
      provider: this.engine.getProviderName(),
      providers: this.engine.listStreamingProviders(),
    });
    if (this.props.onReady && !alreadyAttached) {
      this.props.onReady({
        engine: this.engine,
        facade: this._facade || createPlaybackFacade(this.engine, this.casting),
        plugins: this.pluginHost.list(),
        casting: this.casting,
        provider: this.engine.getProviderName(),
        bus: this.pluginHost.bus,
        tracks: this.tracks,
        spatial: this.spatial,
        api: {
          play: this._transport.play,
          pause: this._transport.pause,
          seek: this._transport.seek,
          setVolume: this._transport.setVolume,
          setMuted: this._transport.setMuted,
          getCurrentTime: function () {
            return (self._getMergedState().currentTime || 0);
          },
          getDuration: function () {
            return (self._getMergedState().duration || 0);
          },
          setPlaybackRate: function (r) {
            return self.engine.setPlaybackRate(r);
          },
          enterFullscreen: function () {
            return self.engine.toggleFullscreen(self.shellRef.current);
          },
          exitFullscreen: function () {
            var st = self._getMergedState();
            if (st.fullscreen) {
              return self.engine.toggleFullscreen(self.shellRef.current);
            }
            return Promise.resolve();
          },
          destroy: function () {
            if (self.engine) self.engine.destroy();
          },
          isReady: function () {
            var s = self._getMergedState().playbackState;
            return (
              s === PlaybackState.READY ||
              s === PlaybackState.PLAYING ||
              s === PlaybackState.PAUSED ||
              s === PlaybackState.BUFFERING ||
              s === PlaybackState.SEEKING
            );
          },
          setQualityLevel: this.engine.setQualityLevel,
          getQuality: this.engine.getQuality,
          getBufferStats: this.engine.getBufferStats,
          getState: this._getMergedState,
          getTextTracks: this.tracks.getTextTracks,
          selectTextTrack: this.tracks.selectTextTrack,
          getAudioTracks: this.tracks.getAudioTracks,
          selectAudioTrack: this.tracks.selectAudioTrack,
          startCast: function (provider, deviceId) {
            return this.casting && this.casting.start
              ? this.casting.start(provider, deviceId)
              : Promise.reject(new Error("Casting unavailable"));
          }.bind(this),
          stopCast: function () {
            return this.casting && this.casting.stop
              ? this.casting.stop()
              : Promise.resolve();
          }.bind(this),
          getCastDiagnostics: function () {
            return this.casting && this.casting.getDiagnostics
              ? this.casting.getDiagnostics()
              : null;
          }.bind(this),
        },
      });
    }
    this._loadSource(this.state.resolved);
  }

  _loadSource(resolved) {
    var self = this;
    if (!resolved.source || !resolved.source.src) {
      var missing = {
        code: "SOURCE_MISSING",
        message: "No playable media URL",
      };
      tpgTrace("ERROR", missing);
      // Surface error overlay — never leave branded loader spinning forever
      this.setState({
        loadTimedOut: true,
        snapshot: Object.assign({}, this.state.snapshot, {
          playbackState: PlaybackState.ERROR,
          error: missing,
        }),
      });
      if (typeof this.props.onError === "function") {
        this.props.onError(missing);
      }
      return;
    }
    tpgTrace("MEDIA_REQUEST_STARTED", {
      source: resolved.source.src,
      type: resolved.source.type,
      autoplay: resolved.autoplay,
      muted: resolved.muted,
    });
    this._loadedSourceKey = sourceIdentityKey(resolved);
    this.setState({ loadTimedOut: false, watermarkRevealed: false });
    this._armLoadTimeout(resolved);
    if (this._disposeUnmuteArm) {
      this._disposeUnmuteArm();
      this._disposeUnmuteArm = null;
    }
    // New source → re-apply host VTT (if any) and force captions OFF.
    this._resetSubtitlesForSource(resolved);
    this.engine
      .load({
        src: resolved.source.src,
        type: resolved.source.type,
        poster: resolved.poster || resolved.source.poster,
      })
      .then(function () {
        self._clearLoadTimeout();
        // Ensure ControlBar sees post-bind capabilities (PiP for progressive/HLS)
        self._refreshSnapshot();
        self._resetSubtitlesForSource(resolved);
        self._syncProviderSubtitleTracks();
        tpgTrace("ACTIVE_PROVIDER", {
          provider: self.engine.getProviderName
            ? self.engine.getProviderName()
            : null,
          surfaceKind:
            self.engine.getState && self.engine.getState().surfaceKind,
          supportsPiP: !!(
            self.engine.getCapabilities &&
            self.engine.getCapabilities().supportsPiP
          ),
        });
        if (resolved.startTime) {
          self.engine.seek(resolved.startTime);
        }
        if (!resolved.autoplay) {
          tpgTrace("STATE", { message: "autoplay_disabled_skip_play" });
          return;
        }
        var startMuted = !!resolved.muted;
        tpgTrace("AUTOPLAY_REQUESTED", {
          muted: startMuted,
          source: resolved.source.src,
        });
        return self
          ._transport.play()
          .catch(function (playErr) {
            // Browsers block unmuted autoplay — mute once and retry so playback starts.
            // This is the ONLY place muted becomes true after an unmuted request.
            var errName =
              (playErr && (playErr.name || playErr.code || playErr.message)) ||
              "";
            tpgTrace("STATE", {
              message: "autoplay_blocked_mute_retry",
              applicationRequestedMuted: startMuted,
              browserMuteRetry: true,
              muteDecisionSource: startMuted
                ? "application_requested_muted"
                : "browser_rejected_unmuted_autoplay",
              requestedMuted: startMuted,
              errName: String(errName),
              source: "browser_or_play_rejection",
            });
            // Legitimate browser-policy fallback only — application already
            // requested unmuted when startMuted was false.
            self.engine.setMuted(true);
            startMuted = true;
            return self._transport.play().then(
              function () {
                self._afterMutedAutoplay();
              },
              function () {
                tpgTrace("ERROR", { message: "autoplay_mute_retry_failed" });
                if (self.pluginHost) {
                  self.pluginHost.bus.emit(PlayerEvent.ANNOUNCEMENT, {
                    message: "Tap play to start",
                  });
                }
              }
            );
          })
          .then(function () {
            if (startMuted) {
              self._afterMutedAutoplay();
            }
          });
      })
      .catch(function (err) {
        self._clearLoadTimeout();
        tpgTrace("ERROR", {
          message: "load_failed",
          err: err,
        });
        if (typeof self.props.onError === "function") {
          self.props.onError(err || { message: "Failed to load media" });
        }
      });
  }

  _afterMutedAutoplay() {
    var self = this;
    if (this._disposeUnmuteArm) {
      this._disposeUnmuteArm();
      this._disposeUnmuteArm = null;
    }
    // Restore audio immediately when policy allows; otherwise arm next gesture.
    var restored = attemptRestoreAudio(this.engine);
    if (restored) {
      if (typeof this.props.onAudioUnlocked === "function") {
        this.props.onAudioUnlocked();
      }
      if (this.pluginHost) {
        this.pluginHost.bus.emit(PlayerEvent.ANNOUNCEMENT, {
          message: "",
        });
      }
      return;
    }
    if (this.pluginHost) {
      this.pluginHost.bus.emit(PlayerEvent.ANNOUNCEMENT, {
        message: "Tap anywhere for sound",
      });
    }
    // Prefer Host-owned arm (survives intro→main remount). Fall back to shell.
    if (typeof this.props.onNeedGestureUnmute === "function") {
      this.props.onNeedGestureUnmute();
      return;
    }
    if (this.shellRef && this.shellRef.current) {
      this._disposeUnmuteArm = armUnmuteOnNextUserGesture(
        this.shellRef.current,
        function () {
          self.engine.setMuted(false);
          if (typeof self.props.onAudioUnlocked === "function") {
            self.props.onAudioUnlocked();
          }
          if (self.pluginHost) {
            self.pluginHost.bus.emit(PlayerEvent.ANNOUNCEMENT, {
              message: "",
            });
          }
        }
      );
    }
  }

  handlePlayPause() {
    var self = this;
    var st = this._getMergedState();
    if (st.playbackState === PlaybackState.PLAYING) {
      this._transport.pause();
      return;
    }
    var features = this.state.resolved.features || {};
    var wantFs = resolveFullscreenOnFirstPlay(features.fullscreenOnFirstPlay);
    this._transport.play().then(
      function () {
        if (!wantFs || self._firstPlayFullscreenDone) return;
        self._firstPlayFullscreenDone = true;
        persistFullscreenOnFirstPlay(true);
        // Must run in the same user-gesture chain when possible
        try {
          self.engine.toggleFullscreen(self.shellRef.current);
        } catch (e) {
          /* browser may still reject */
        }
      },
      function () {}
    );
  }

  handleSeek(t) {
    this._transport.seek(t);
  }

  handleSkip(delta) {
    var st = this._getMergedState();
    this._transport.seek(Math.max(0, (st.currentTime || 0) + delta));
  }

  handleSeekZone(side) {
    var skip = this.state.resolved.features.skipInterval || 10;
    this.handleSkip(side === "left" ? -skip : skip);
  }

  handleVolume(v) {
    this._transport.setMuted(false);
    this._transport.setVolume(v);
    if (typeof this.props.onAudioUnlocked === "function") {
      this.props.onAudioUnlocked();
    }
  }

  handleToggleMute() {
    var st = this._getMergedState();
    if (st.muted) {
      applyUnmuteToEngine(this.engine, st.volume || 1);
      if (this.state.announcement === "Tap anywhere for sound") {
        this.setState({ announcement: "" });
      }
      if (typeof this.props.onAudioUnlocked === "function") {
        this.props.onAudioUnlocked();
      }
      return;
    }
    this._transport.setMuted(true);
  }

  handleRate(r) {
    this.engine.setPlaybackRate(r);
  }

  handleQualityChange(level) {
    this.engine.setQualityLevel(level);
  }

  handleTextTrackChange(id) {
    var catalog =
      this.tracks && typeof this.tracks.getTextTracks === "function"
        ? this.tracks.getTextTracks()
        : this.state.textTracks || [];
    if (id === "on") {
      if (catalog[0]) {
        id = catalog[0].id;
      } else {
        this._announceSubtitlesUnavailable();
        return;
      }
    }
    if (id && id !== "off" && !catalog.length) {
      this._announceSubtitlesUnavailable();
      return;
    }
    if (this.tracks && typeof this.tracks.selectTextTrack === "function") {
      this.tracks.selectTextTrack(id);
    }
    if (this.engine && typeof this.engine.setProviderSubtitles === "function") {
      var active =
        this.tracks && this.tracks.getActiveTextTrackId
          ? this.tracks.getActiveTextTrackId()
          : null;
      this.engine.setProviderSubtitles(active || "off");
    }
    if (this.pluginHost) {
      this.pluginHost.bus.emit("ui:selectTextTrack", id);
    }
  }

  _announceSubtitlesUnavailable() {
    if (this.tracks && typeof this.tracks.selectTextTrack === "function") {
      this.tracks.selectTextTrack("off");
    }
    if (this.engine && typeof this.engine.setProviderSubtitles === "function") {
      this.engine.setProviderSubtitles("off");
    }
    this.setState({ activeTextId: null });
    if (this.pluginHost) {
      this.pluginHost.bus.emit(PlayerEvent.ANNOUNCEMENT, {
        message: "Subtitles are not available for this video at this time.",
        ttlMs: 4000,
      });
    }
  }

  _resetSubtitlesForSource(resolved) {
    if (this.tracks && typeof this.tracks.setTextTracks === "function") {
      this.tracks.setTextTracks((resolved && resolved.subtitles) || []);
    }
    if (this.engine && typeof this.engine.setProviderSubtitles === "function") {
      this.engine.setProviderSubtitles("off");
    }
    var nextTracks =
      this.tracks && typeof this.tracks.getTextTracks === "function"
        ? this.tracks.getTextTracks()
        : [];
    this.setState({
      activeTextId: null,
      textTracks: nextTracks,
    });
  }

  _syncProviderSubtitleTracks() {
    if (
      !this.tracks ||
      typeof this.tracks.mergeProviderSubtitleTracks !== "function"
    ) {
      return;
    }
    var list =
      this.engine && typeof this.engine.getProviderSubtitleTracks === "function"
        ? this.engine.getProviderSubtitleTracks()
        : [];
    this.tracks.mergeProviderSubtitleTracks(list || []);
  }

  handleAudioTrackChange(id) {
    if (this.pluginHost) {
      this.pluginHost.bus.emit("ui:selectAudioTrack", id);
    }
  }

  handleChapterSelect(id) {
    if (this.pluginHost) {
      this.pluginHost.bus.emit("ui:selectChapter", id);
    }
  }

  handleCast() {
    var self = this;
    if (!this.casting || !this.casting.start) {
      return;
    }
    if (this.casting.isRemote && this.casting.isRemote()) {
      this.casting.stop().catch(function () {});
      return;
    }
    // Prefer simulated (lab), then chromecast, then airplay
    var providers =
      (this.casting.getProviders && this.casting.getProviders()) || [];
    var order = ["simulated", "chromecast", "airplay"];
    var chosen = null;
    for (var i = 0; i < order.length; i++) {
      for (var j = 0; j < providers.length; j++) {
        if (providers[j].name === order[i] && providers[j].available) {
          chosen = order[i];
          break;
        }
      }
      if (chosen) break;
    }
    if (!chosen) {
      if (this.pluginHost) {
        this.pluginHost.bus.emit(PlayerEvent.ANNOUNCEMENT, {
          message: "No cast devices available",
        });
      }
      return;
    }
    this.casting.start(chosen).catch(function (err) {
      if (self.pluginHost) {
        self.pluginHost.bus.emit(PlayerEvent.ANNOUNCEMENT, {
          message: (err && err.message) || "Cast failed",
        });
      }
    });
  }

  handleFullscreen() {
    this.engine.toggleFullscreen(this.shellRef.current);
  }

  handleTheater() {
    var next = !this.state.theaterMode;
    persistTheaterMode(next);
    // When Host owns theaterMode via prop, only notify — Host re-renders with prop
    if (this.props.theaterMode != null) {
      if (typeof this.props.onTheaterChange === "function") {
        this.props.onTheaterChange(next);
      }
      return;
    }
    this.setState({ theaterMode: next });
    if (typeof this.props.onTheaterChange === "function") {
      this.props.onTheaterChange(next);
    }
  }

  handleRetry() {
    this.setState({ loadTimedOut: false });
    this._loadSource(this.state.resolved);
  }

  handlePiP() {
    var self = this;
    var shell = this.shellRef && this.shellRef.current;
    var caps =
      this.engine && typeof this.engine.getCapabilities === "function"
        ? this.engine.getCapabilities()
        : null;
    var providerName =
      this.engine && typeof this.engine.getProviderName === "function"
        ? this.engine.getProviderName()
        : null;
    var engSt =
      this.engine && this.engine.getState ? this.engine.getState() : {};
    var surfaceKind = engSt.surfaceKind;
    var videoEl =
      this.engine && typeof this.engine.getVideoElement === "function"
        ? this.engine.getVideoElement()
        : null;
    tpgTrace("PIP_ENTER_REQUEST", {
      provider: providerName,
      surfaceKind: surfaceKind,
      supportsPiP: caps ? caps.supportsPiP : null,
      controller: "NextGenPlayer.handlePiP",
    });
    tpgTrace("CURRENT_ENGINE", { hasEngine: !!this.engine });
    tpgTrace("CURRENT_PROVIDER", { provider: providerName });
    tpgTrace("PIP_TARGET", {
      target: videoEl ? "HTMLVideoElement" : "none",
      videoSrc: videoEl ? String(videoEl.currentSrc || videoEl.src || "").slice(0, 80) : null,
      videoConnected: !!(videoEl && videoEl.isConnected),
    });
    tpgTrace("PIP_BOUND_ELEMENT", {
      tag: videoEl ? videoEl.tagName : null,
      muted: videoEl ? !!videoEl.muted : null,
      paused: videoEl ? !!videoEl.paused : null,
      readyState: videoEl ? videoEl.readyState : null,
    });
    tpgTrace("CONTROLBAR_REFRESH", {
      snapshotProvider: this.state.snapshot && this.state.snapshot.providerName,
      snapshotSurface:
        this.state.snapshot && this.state.snapshot.surfaceKind,
      snapshotSupportsPiP:
        this.state.snapshot &&
        this.state.snapshot.capabilities &&
        this.state.snapshot.capabilities.supportsPiP,
    });
    // Embeds only — never block progressive/HLS on idle capability defaults
    if (
      surfaceKind === "embed" ||
      providerName === "youtube-embed" ||
      providerName === "vimeo-embed" ||
      (providerName && caps && caps.supportsPiP === false && surfaceKind === "embed")
    ) {
      tpgTrace("PIP_ENTER_FAILURE", {
        reason: "embed_unsupported",
        provider: providerName,
      });
      if (this.pluginHost) {
        this.pluginHost.bus.emit(PlayerEvent.ANNOUNCEMENT, {
          message: "Picture-in-Picture is not available for this video",
        });
      }
      return;
    }
    // Ensure we target the live main video element (post-handoff remount)
    if (videoEl && videoEl.style && videoEl.style.display === "none") {
      videoEl.style.display = "";
    }
    this.engine.togglePictureInPicture(shell).catch(function (err) {
      tpgTrace("PIP_ENTER_FAILURE", {
        code: err && err.code,
        message: err && err.message,
        provider: providerName,
      });
      if (self.pluginHost) {
        self.pluginHost.bus.emit(PlayerEvent.ANNOUNCEMENT, {
          message:
            (err && err.code === "PIP_UNSUPPORTED") ||
            (err && String(err.message || "").indexOf("PiP") !== -1)
              ? "Picture-in-Picture is not available for this video"
              : "Could not enter Picture-in-Picture",
        });
      }
    });
  }

  render() {
    var resolved = this.state.resolved;
    var theme = createTheme(
      Object.assign({}, resolved.theme, {
        branding: Object.assign(
          {},
          resolved.theme.branding || {},
          resolved.branding || {}
        ),
      })
    );
    var st = this.state.snapshot;
    var brand = resolved.branding || {};
    var brandLabel =
      brand.label ||
      (theme.branding && theme.branding.wordmark) ||
      null;
    var watermarkUrl =
      resolved.features.watermarks && brand.watermark
        ? brand.watermark
        : null;
    var wmPosClass = watermarkPositionClass(brand.watermarkPosition);
    var wmOpacity =
      brand.watermarkOpacity != null ? brand.watermarkOpacity : 0.85;
    var wmSize =
      brand.watermarkSize != null ? brand.watermarkSize : 12;
    var fadeWatermark = brand.watermarkFadeIn !== false;

    var controlScale =
      resolved.features.tvNavigation && !resolved.features.controlScale
        ? 1.25
        : resolved.features.controlScale || 1;
    if (resolved.features.tvNavigation && resolved.features.controlScale) {
      controlScale = resolved.features.controlScale;
    } else if (resolved.features.tvNavigation) {
      controlScale = Math.max(controlScale, 1.25);
    }

    var shellStyle = {
      background: theme.colors.background,
      color: theme.colors.text,
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.fontSize,
      ["--ngp-accent"]: brand.accent || theme.colors.accent,
      ["--ngp-control-scale"]: String(controlScale),
      ["--ngp-watermark-opacity"]: String(wmOpacity),
      // Percent of .ngp-shell width (Admin Watermark Size %). CSS must not px-clamp this.
      ["--ngp-watermark-size"]: wmSize + "%",
    };

    if (resolved.theme && resolved.theme.subtitles && this.shellRef.current) {
      this.tracks &&
        this.tracks.applySubtitleTheme(
          this.shellRef.current,
          resolved.theme.subtitles
        );
    }

    var highContrast = !!resolved.features.highContrast;
    var tvNav = !!resolved.features.tvNavigation;
    var theater = !!this.state.theaterMode;
    var isLoading =
      st.playbackState === PlaybackState.LOADING ||
      st.playbackState === PlaybackState.IDLE ||
      (!this.state.surfaceReady && st.playbackState !== PlaybackState.ERROR);
    var isBuffering = st.playbackState === PlaybackState.BUFFERING;
    var showBrandedLoader =
      !!resolved.features.brandedLoading &&
      (isLoading || (isBuffering && st.currentTime < 0.5)) &&
      !this.state.loadTimedOut &&
      st.playbackState !== PlaybackState.ERROR &&
      st.playbackState !== PlaybackState.PLAYING;
    var showError =
      st.playbackState === PlaybackState.ERROR || this.state.loadTimedOut;
    var errorMessage =
      (st.error && (st.error.message || st.error.code)) ||
      (this.state.loadTimedOut
        ? "This video is taking too long to load. Check your connection and try again."
        : "Playback error");

    return (
      <div
        className={
          "ngp-shell" +
          (highContrast ? " ngp-shell--contrast" : "") +
          (tvNav ? " ngp-shell--tv" : "") +
          (theater ? " ngp-shell--theater" : "") +
          (this.state.controlsVisible ? "" : " ngp-shell--idle") +
          (showBrandedLoader ? " ngp-shell--loading" : "")
        }
        ref={this.shellRef}
        style={shellStyle}
        data-state={st.playbackState}
        data-provider={st.providerName || ""}
        data-cast-target={st.castTarget || CastingTarget.LOCAL}
        data-theater={theater ? "1" : "0"}
        tabIndex={0}
        role="region"
        aria-label={resolved.title || "Video player"}
      >
        <div className="ngp-sr-only" aria-live="polite" aria-atomic="true">
          {this.state.announcement}
        </div>
        {this.state.announcement ? (
          <div className="ngp-unmute-hint" role="status" aria-hidden="true">
            {this.state.announcement}
          </div>
        ) : null}

        {/* Site brand chip duplicates Admin watermark when both use the same
            Pentecost+ mark in the top-left. Prefer the Admin watermark overlay. */}
        {brandLabel && !watermarkUrl ? (
          <div className="ngp-brand" aria-hidden="true">
            {brand.logo ? (
              <img className="ngp-brand-logo" src={brand.logo} alt="" />
            ) : (
              <span>{brandLabel}</span>
            )}
          </div>
        ) : null}
        {/* Keep watermarkUrl truthy during intro so .ngp-brand stays suppressed,
            but do not paint the Admin watermark over the cinematic intro. */}
        {watermarkUrl && !this.props.isCinematicIntro ? (
          <div
            className={
              "ngp-watermark " +
              wmPosClass +
              (fadeWatermark && !this.state.watermarkRevealed
                ? " ngp-watermark--hidden"
                : " ngp-watermark--visible")
            }
            data-watermark-size={wmSize}
            style={{ width: wmSize + "%" }}
            aria-hidden="true"
          >
            <img src={watermarkUrl} alt="" />
          </div>
        ) : null}

        <div className="ngp-stage">
          {resolved.features.seekZones !== false ? (
            <React.Fragment>
              <button
                type="button"
                className="ngp-seek-zone ngp-seek-zone--left"
                aria-label={
                  "Seek back " +
                  (resolved.features.skipInterval || 10) +
                  " seconds"
                }
                onDoubleClick={function (e) {
                  e.preventDefault();
                  this.handleSeekZone("left");
                }.bind(this)}
              />
              <button
                type="button"
                className="ngp-seek-zone ngp-seek-zone--right"
                aria-label={
                  "Seek forward " +
                  (resolved.features.skipInterval || 10) +
                  " seconds"
                }
                onDoubleClick={function (e) {
                  e.preventDefault();
                  this.handleSeekZone("right");
                }.bind(this)}
              />
            </React.Fragment>
          ) : null}
          <MediaSurface
            poster={resolved.poster}
            onAttach={this.handleAttach}
          />
          {showBrandedLoader ? (
            <div className="ngp-branded-loader" aria-live="polite">
              <div className="ngp-branded-loader__inner">
                {brand.logo ? (
                  <img
                    className="ngp-branded-loader__logo"
                    src={brand.logo}
                    alt=""
                  />
                ) : brandLabel ? (
                  <div className="ngp-branded-loader__wordmark">{brandLabel}</div>
                ) : null}
                <div className="ngp-branded-loader__spinner" aria-hidden="true" />
                <p className="ngp-branded-loader__label">Loading…</p>
              </div>
            </div>
          ) : null}
          {showError ? (
            <div className="ngp-error-overlay" role="alert">
              <p className="ngp-error-overlay__msg">{errorMessage}</p>
              <button
                type="button"
                className="ngp-error-overlay__retry"
                onClick={this.handleRetry}
              >
                Try again
              </button>
            </div>
          ) : null}
        </div>

        <ControlBar
          state={st}
          features={resolved.features}
          controlsVisible={this.state.controlsVisible}
          theaterMode={theater}
          textTracks={this.state.textTracks}
          activeTextId={this.state.activeTextId || "off"}
          audioTracks={this.state.audioTracks}
          activeAudioId={this.state.activeAudioId}
          chapters={this.state.chapters}
          castActive={this.state.castActive}
          castProviderName={this.state.castProviderName}
          style={{
            background: theme.colors.surface,
            minHeight: (theme.controls.height || 48) * controlScale,
          }}
          onPlayPause={this.handlePlayPause}
          onSeek={this.handleSeek}
          onSkip={this.handleSkip}
          onVolumeChange={this.handleVolume}
          onToggleMute={this.handleToggleMute}
          onRateChange={this.handleRate}
          onQualityChange={this.handleQualityChange}
          onTextTrackChange={this.handleTextTrackChange}
          onAudioTrackChange={this.handleAudioTrackChange}
          onChapterSelect={this.handleChapterSelect}
          onFullscreen={this.handleFullscreen}
          onTheater={this.handleTheater}
          onPiP={this.handlePiP}
          onCast={this.handleCast}
        />
      </div>
    );
  }
}

export default NextGenPlayer;
